/**
 * Reward Catalog API Service
 * Service for reward catalog API calls using centralized Axios-based API service
 *
 * API Endpoints (from Postman collection):
 * - GET /product/api/v1/rewards/catalog - List vouchers (paginated)
 * - GET /product/api/v1/rewards/catalog/:voucherId - Get voucher detail
 * - POST /product/api/v1/rewards/catalog - Create voucher
 * - PUT /product/api/v1/rewards/catalog/:voucherId - Update voucher
 */

import { injectable } from "inversify";
import { config } from "@/config";
import {
  apiClient,
  apiService,
  createRequestConfig,
  type PaginatedApiResponse,
  type ApiResponse,
} from "@/remote/api";
import type {
  VoucherListItemDto,
  VoucherDetailDto,
  CreateVoucherRequestDto,
  CreateVoucherResponseDto,
  UpdateVoucherRequestDto,
  UpdateVoucherResponseDto,
  VoucherListQueryParams,
  CreateEWalletRequestDto,
  CreateEWalletResponseDto,
  CreatePointConversionRequestDto,
  CreatePointConversionResponseDto,
  EWalletProviderDto,
  PointConversionPartnerDto,
  MerchantDropdownDto,
  VoucherCategoryDropdownDto,
  ValidateVoucherCodesFileResponseDto,
  VoucherkuCategoryDto,
  VoucherkuProductsResponseDto,
  ImportVoucherkuRequestDto,
  ImportVoucherkuResponseDto,
} from "../response";
import { mockVoucherListData, mockVoucherDetailData } from "../../../mock/rewardCatalogMock";

@injectable()
export class RewardCatalogApiService {
  private readonly useMockData: boolean;

  constructor() {
    this.useMockData = config.features.useMockData;
  }

  /**
   * Fetch voucher list with pagination
   * Endpoint: GET /product/api/v1/rewards/catalog
   */
  async fetchVoucherList(
    params?: VoucherListQueryParams
  ): Promise<PaginatedApiResponse<VoucherListItemDto>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(mockVoucherListData), 300);
      });
    }

    return apiService.getPaginated<VoucherListItemDto>(
      "/product/api/v1/rewards/catalog",
      {
        params: {
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? 10,
          ...(params?.status && { status: params.status }),
          ...(params?.search && { search: params.search }),
          ...(params?.brandId && { brandId: params.brandId }),
          ...(params?.sort && { sort: params.sort }),
        },
      }
    );
  }

  /**
   * Fetch voucher detail by ID
   * Endpoint: GET /product/api/v1/rewards/catalog/:voucherId
   */
  async fetchVoucherDetail(voucherId: string): Promise<ApiResponse<VoucherDetailDto>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: mockVoucherDetailData,
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.get<VoucherDetailDto>(
      `/product/api/v1/rewards/catalog/${voucherId}`
    );
  }

  /**
   * Create new voucher
   * Endpoint: POST /product/api/v1/rewards/catalog
   */
  async createVoucher(
    data: CreateVoucherRequestDto
  ): Promise<ApiResponse<CreateVoucherResponseDto>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: {
                voucherId: `VOU-${Date.now()}`,
                title: data.title,
                status: data.status,
                createdAt: new Date().toISOString(),
              },
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.post<CreateVoucherResponseDto, CreateVoucherRequestDto>(
      "/product/api/v1/rewards/catalog",
      data
    );
  }

  /**
   * Update existing voucher
   * Endpoint: PUT /product/api/v1/rewards/catalog/:voucherId
   */
  async updateVoucher(
    voucherId: string,
    data: UpdateVoucherRequestDto
  ): Promise<ApiResponse<UpdateVoucherResponseDto>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: {
                voucherId,
                title: data.title,
                status: data.status,
                updatedAt: new Date().toISOString(),
              },
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.put<UpdateVoucherResponseDto, UpdateVoucherRequestDto>(
      `/product/api/v1/rewards/catalog/${voucherId}`,
      data
    );
  }

  /**
   * Delete voucher
   * Endpoint: DELETE /product/api/v1/rewards/catalog/:voucherId
   */
  async deleteVoucher(voucherId: string): Promise<void> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(), 300);
      });
    }

    await apiService.deleteData<void>(
      `/product/api/v1/rewards/catalog/${voucherId}`
    );
  }

  /**
   * Create E-Wallet conversion rate
   * Endpoint: POST /product/api/v1/rewards/ewallet/conversions
   */
  async createEWalletConversion(
    data: CreateEWalletRequestDto
  ): Promise<ApiResponse<CreateEWalletResponseDto>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: {
                conversionId: Date.now(),
                providerId: data.providerId,
                pointsRequired: data.pointsRequired,
                rupiahValue: data.rupiahValue,
              },
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.post<CreateEWalletResponseDto, CreateEWalletRequestDto>(
      "/product/api/v1/rewards/ewallet/conversions",
      data
    );
  }

  /**
   * Create Point Conversion rate
   * Endpoint: POST /product/api/v1/rewards/point-conversion/rates
   */
  async createPointConversionRate(
    data: CreatePointConversionRequestDto
  ): Promise<ApiResponse<CreatePointConversionResponseDto>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: {
                rateId: Date.now(),
                rateText: `${data.fromPoints} SwaraPoint → ${data.toPoints} Partner Point`,
              },
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.post<CreatePointConversionResponseDto, CreatePointConversionRequestDto>(
      "/product/api/v1/rewards/point-conversion/rates",
      data
    );
  }

  /**
   * Fetch E-Wallet providers list for dropdown
   * Endpoint: GET /product/api/v1/rewards/ewallet/providers/dropdown
   */
  async fetchEWalletProviders(): Promise<ApiResponse<EWalletProviderDto[]>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: [
                {
                  providerId: "gopay",
                  providerName: "GoPay",
                },
                {
                  providerId: "ovo",
                  providerName: "OVO",
                },
                {
                  providerId: "dana",
                  providerName: "DANA",
                },
                {
                  providerId: "shopeepay",
                  providerName: "ShopeePay",
                },
              ],
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.get<EWalletProviderDto[]>(
      "/product/api/v1/rewards/ewallet/providers/dropdown"
    );
  }

  /**
   * Fetch Point Conversion partners list for dropdown
   * Endpoint: GET /product/api/v1/rewards/point-conversion/partners/dropdown
   */
  async fetchPointConversionPartners(): Promise<ApiResponse<PointConversionPartnerDto[]>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: [
                {
                  partnerId: "livin",
                  partnerName: "Livin' by Mandiri",
                },
                {
                  partnerId: "allo",
                  partnerName: "Allo Bank",
                },
                {
                  partnerId: "digistar",
                  partnerName: "Digistar",
                },
              ],
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.get<PointConversionPartnerDto[]>(
      "/product/api/v1/rewards/point-conversion/partners/dropdown"
    );
  }

  /**
   * Fetch merchants dropdown list
   * Endpoint: GET /product/api/v1/merchants/dropdown
   */
  async fetchMerchantsDropdown(): Promise<ApiResponse<MerchantDropdownDto[]>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: [
                { merchantId: "M001", merchantName: "Kopi Kangen" },
                { merchantId: "M002", merchantName: "Bento Oke" },
                { merchantId: "M003", merchantName: "Ramen Enak" },
                { merchantId: "M004", merchantName: "Teh Pinggir Wangi" },
                { merchantId: "M005", merchantName: "Bakso Mantap" },
              ],
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.get<MerchantDropdownDto[]>(
      "/product/api/v1/merchants/dropdown"
    );
  }

  /**
   * Fetch voucher categories dropdown list
   * Endpoint: GET /v1/voucher-categories/dropdown
   */
  async fetchVoucherCategoriesDropdown(): Promise<ApiResponse<VoucherCategoryDropdownDto[]>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: [
                { categoryId: "cat-fnb", name: "Food & Beverage" },
                { categoryId: "cat-fashion", name: "Fashion" },
                { categoryId: "cat-ent", name: "Entertainment" },
                { categoryId: "cat-travel", name: "Travel" },
                { categoryId: "cat-health", name: "Health & Beauty" },
                { categoryId: "cat-mart", name: "Groceries" },
              ],
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.get<VoucherCategoryDropdownDto[]>(
      "/product/api/v1/voucher-categories/dropdown"
    );
  }

  /**
   * Validate voucher codes file (CSV or XLSX)
   * Endpoint: POST /product/api/v1/rewards/catalog/validate-file
   */
  async validateVoucherCodesFile(
    file: File
  ): Promise<ApiResponse<ValidateVoucherCodesFileResponseDto>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: {
                totalCodes: 500,
                validCodes: 495,
                invalidCodes: 5,
                isValid: true,
                errors: [],
                warnings: ["5 duplicate codes were found and will be skipped"],
                duplicateCodes: ["CODE001", "CODE002", "CODE003", "CODE004", "CODE005"],
                voucherCodes: ["CODE006", "CODE007", "CODE008", "CODE009", "CODE010"],
                fileName: file.name,
                fileSize: file.size,
              },
              meta: { code: "20000", message: "OK" },
            }),
          1000
        );
      });
    }

    const formData = new FormData();
    formData.append("file", file);

    return apiService.post<ValidateVoucherCodesFileResponseDto, FormData>(
      "/product/api/v1/rewards/catalog/validate-file",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  }

  /**
   * Download voucher code template file
   * Endpoint: GET /product/api/v1/rewards/catalog/template
   */
  async downloadVoucherTemplate(): Promise<Blob> {
    if (this.useMockData) {
      return new Blob([
        "voucher_code\nVOUCHER001\nVOUCHER002\n",
      ], { type: "text/csv;charset=utf-8" });
    }

    const response = await apiClient.get<Blob>(
      "/product/api/v1/rewards/catalog/template",
      createRequestConfig(undefined, {
        responseType: "blob",
      })
    );

    return response.data;
  }

  /**
   * Fetch voucherku categories list
   * Endpoint: GET /v1/voucherku/categories
   */
  async fetchVoucherkuCategories(): Promise<ApiResponse<readonly VoucherkuCategoryDto[]>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: [
                { name: "Food & Beverage", category_id: "cat-001" },
                { name: "Entertainment", category_id: "cat-002" },
                { name: "Lifestyle", category_id: "cat-003" },
                { name: "Travel", category_id: "cat-004" },
              ],
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.get<readonly VoucherkuCategoryDto[]>("/product/api/v1/voucherku/categories");
  }

  /**
   * Fetch voucherku products
   * Endpoint: POST /v1/voucherku/products
   */
  async fetchVoucherkuProducts(
    categoryId?: string
  ): Promise<ApiResponse<VoucherkuProductsResponseDto>> {
    if (this.useMockData) {
      const mockProducts = [
        {
          product_code: "VK-001",
          product_name: "Buy 1 Get 1 Free Cheeseburger",
          category_id: "cat-001",
          category_name: "Food & Beverage",
          price: 25000,
          description: "Get a free cheeseburger when you buy one!",
        },
        {
          product_code: "VK-002",
          product_name: "Free Spanish Latte Regular",
          category_id: "cat-001",
          category_name: "Food & Beverage",
          price: 35000,
          description: "Enjoy a free regular Spanish Latte",
        },
        {
          product_code: "VK-003",
          product_name: "Movie Ticket Discount 50%",
          category_id: "cat-002",
          category_name: "Entertainment",
          price: 50000,
          description: "Get 50% off on any movie ticket",
        },
      ];

      const filteredProducts = categoryId
        ? mockProducts.filter((p) => p.category_id === categoryId)
        : mockProducts;

      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: {
                products: filteredProducts,
                totalProducts: filteredProducts.length,
              },
              meta: { code: "20000", message: "OK" },
            }),
          300
        );
      });
    }

    return apiService.post<VoucherkuProductsResponseDto, { category_id?: string }>(
      "/product/api/v1/voucherku/products",
      categoryId ? { category_id: categoryId } : {}
    );
  }

  /**
   * Import voucherku product as reward
   * Endpoint: POST /v1/voucherku/import
   */
  async importVoucherku(
    data: ImportVoucherkuRequestDto
  ): Promise<ApiResponse<ImportVoucherkuResponseDto>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              data: {
                voucher_id: `vk-${Date.now()}`,
                product_code: data.productCode,
                title: data.title,
                status: data.status,
                created_at: new Date().toISOString(),
              },
              meta: { code: "20000", message: "OK" },
            }),
          500
        );
      });
    }

    return apiService.post<ImportVoucherkuResponseDto, ImportVoucherkuRequestDto>(
      "/product/api/v1/voucherku/import",
      data
    );
  }
}
